import { useAppDispatch, useAppSelector } from "hooks/redux";
import { cloneDeep } from "lodash";
import { useWebsiteBuilderDebouncedUpdate, WebsiteBuilderDebouncedUpdateTime } from "pages/secure/facility/websiteBuilder/editor/hooks/debouncedUpdate/useWebsiteBuilderDebouncedUpdate";
import {
  IWebsiteBuilderSlideshowSettings,
  TWebsiteBuilderSlideshowObjectFit,
  WEBSITE_BUILDER_SLIDESHOW_MAX_BORDER_RADIUS,
  WEBSITE_BUILDER_SLIDESHOW_MAX_TIME_PER_SLIDE,
  WEBSITE_BUILDER_SLIDESHOW_MIN_BORDER_RADIUS,
  WEBSITE_BUILDER_SLIDESHOW_MIN_TIME_PER_SLIDE,
} from "pages/secure/facility/websiteBuilder/editor/nodes/container/websiteBuilderSlideshow";
import React, { useMemo, useState } from "react";
import { Select } from "components/select/index";
import { WEBSITE_BUILDER_PORTAL } from "pages/secure/facility/websiteBuilder/editor/WebsiteBuilderEditor";
import { IWebsiteBuilderSelectOption, renderWebsiteBuilderSelectOptions } from "../../../../../helpers/renderWebsiteBuilderSelectOptions";
import { useTranslation } from "react-i18next";
import { websiteBuilderUpdateContainerSettings } from "redux/actions/websiteBuilder/editor/websiteBuilderEditor";
import { SlideshowImagesSettings } from "./SlideshowImagesSettings";
import Toggle from "components/form/toggle/Toggle";
import { WebsiteBuilderRange } from "pages/secure/facility/websiteBuilder/editor/components/range/WebsiteBuilderRange";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronDown, faChevronUp } from "@fortawesome/pro-regular-svg-icons";
import { WebsiteBuilderHelpIcon } from "pages/secure/facility/websiteBuilder/editor/components/helpIcon/WebsiteBuilderHelpIcon";
import { WebsiteBuilderPanelContent } from "pages/secure/facility/websiteBuilder/editor/shell/panel/content/WebsiteBuilderPanelContent";

interface ISlideshowSettingsProps {
  id: string;
  settings: IWebsiteBuilderSlideshowSettings;
  closeSettings: () => void;
}

type TTrackedSlideshowSettings = Omit<IWebsiteBuilderSlideshowSettings, "images">;

export function SlideshowSettings(props: ISlideshowSettingsProps) {
  const { id, settings, closeSettings } = props;

  const { t } = useTranslation();

  const dispatch = useAppDispatch();

  const activePageId = useAppSelector(store => store.websiteBuilderStore.editor.activePageId);
  const deviceView = useAppSelector(store => store.websiteBuilderStore.editor.deviceView);

  const trackedSettings: TTrackedSlideshowSettings = useMemo(() => {
    return {
      objectFit: settings.objectFit,
      borderRadius: settings.borderRadius,
      autoplay: settings.autoplay,
      timePerSlide: settings.timePerSlide,
      showArrows: settings.showArrows,
      showPagination: settings.showPagination,
      expandImageOnClick: settings.expandImageOnClick,
    };
  }, [
    settings.objectFit,
    settings.borderRadius,
    settings.autoplay,
    settings.timePerSlide,
    settings.showArrows,
    settings.showPagination,
    settings.expandImageOnClick,
  ]);

  const [updatedTrackedSettings, setUpdatedTrackedSettings] = useState<TTrackedSlideshowSettings>({
    objectFit: settings.objectFit,
    borderRadius: settings.borderRadius,
    autoplay: settings.autoplay,
    timePerSlide: settings.timePerSlide,
    showArrows: settings.showArrows,
    showPagination: settings.showPagination,
    expandImageOnClick: settings.expandImageOnClick,
  });

  const [moreSettingsOpen, setMoreSettingsOpen] = useState(false);

  const objectFitOptions: IWebsiteBuilderSelectOption<TWebsiteBuilderSlideshowObjectFit>[] = useMemo(() => {
    return [
      {
        label: t("Cover"),
        value: "cover",
      },
      {
        label: t("Contain"),
        value: "contain",
      },
      {
        label: t("Fill"),
        value: "fill",
      },
    ];
  }, []);

  function dispatchSettings(
    prevTrackedSettings: TTrackedSlideshowSettings,
    nextTrackedSettings: TTrackedSlideshowSettings,
  ) {
    dispatch(
      websiteBuilderUpdateContainerSettings(activePageId, deviceView, id, {
        id,
        prevContainerSettings: {
          type: "slideshow",
          settings: {
            ...cloneDeep(prevTrackedSettings),
            images: cloneDeep(settings.images),
          },
        },
        nextContainerSettings: {
          type: "slideshow",
          settings: {
            ...cloneDeep(nextTrackedSettings),
            images: cloneDeep(settings.images),
          },
        },
      }),
    );
  }

  const { handleUpdateState, handleDebouncedUpdateState } = useWebsiteBuilderDebouncedUpdate({
    state: trackedSettings,
    updatedState: updatedTrackedSettings,
    setUpdatedState: setUpdatedTrackedSettings,
    dispatchState: dispatchSettings,
    debouncedUpdateTime: WebsiteBuilderDebouncedUpdateTime.MEDIUM,
  });

  function handleChangeBorderRadius(borderRadius: number) {
    handleDebouncedUpdateState(prev => ({
      ...prev,
      borderRadius,
    }));
  }

  function renderImagesSettings() {
    return (
      <SlideshowImagesSettings
        id={id}
        settings={settings}
      />
    );
  }

  function renderGeneralSettings() {
    return (
      <>
        <Select
          label={t("Fit")}
          onChange={(objectFit: TWebsiteBuilderSlideshowObjectFit) =>
            handleUpdateState(prev => ({
              ...prev,
              objectFit,
            }))
          }
          defaultValue={updatedTrackedSettings.objectFit}
          usePortal
          portalId={WEBSITE_BUILDER_PORTAL}
        >
          {renderWebsiteBuilderSelectOptions(objectFitOptions)}
        </Select>
        <WebsiteBuilderRange
          label={t("Border radius (px)")}
          min={WEBSITE_BUILDER_SLIDESHOW_MIN_BORDER_RADIUS}
          max={WEBSITE_BUILDER_SLIDESHOW_MAX_BORDER_RADIUS}
          step={1}
          value={updatedTrackedSettings.borderRadius}
          setValue={handleChangeBorderRadius}
        />
      </>
    );
  }

  function renderMoreSettings() {
    return (
      <div className="website-builder-slideshow-more-settings">
        <button
          type="button"
          className="more-settings-trigger"
          aria-expanded={moreSettingsOpen}
          onClick={() => setMoreSettingsOpen(prev => !prev)}
        >
          <span>{t("More settings")}</span>
          <FontAwesomeIcon icon={moreSettingsOpen ? faChevronUp : faChevronDown} />
        </button>
        {moreSettingsOpen && (
          <div className="more-settings-content">
            <Toggle
              labelRight={t("Autoplay slides")}
              checked={updatedTrackedSettings.autoplay}
              onChange={e =>
                handleUpdateState(prev => ({
                  ...prev,
                  autoplay: e.target.checked,
                }))
              }
            />
            {updatedTrackedSettings.autoplay && (
              <div className="time-per-slide-container">
                <WebsiteBuilderRange
                  label={
                    <div className="time-per-slide-label">
                      <div>{t("Time per slide (ms)")}</div>
                      <WebsiteBuilderHelpIcon
                        id="website-builder-slideshow-time-per-slide-help"
                        message={t("Set how long each slide stays visible before moving to the next.")}
                        placement="bottom"
                      />
                    </div>
                  }
                  min={WEBSITE_BUILDER_SLIDESHOW_MIN_TIME_PER_SLIDE}
                  max={WEBSITE_BUILDER_SLIDESHOW_MAX_TIME_PER_SLIDE}
                  step={500}
                  value={updatedTrackedSettings.timePerSlide}
                  setValue={timePerSlide =>
                    handleDebouncedUpdateState(prev => ({
                      ...prev,
                      timePerSlide,
                    }))
                  }
                />
              </div>
            )}
            <Toggle
              labelRight={t("Show arrows")}
              checked={updatedTrackedSettings.showArrows}
              onChange={e =>
                handleUpdateState(prev => ({
                  ...prev,
                  showArrows: e.target.checked,
                }))
              }
            />
            <Toggle
              labelRight={t("Show pagination")}
              checked={updatedTrackedSettings.showPagination}
              onChange={e =>
                handleUpdateState(prev => ({
                  ...prev,
                  showPagination: e.target.checked,
                }))
              }
            />
            <Toggle
              labelRight={t("Expand image on click")}
              checked={updatedTrackedSettings.expandImageOnClick}
              onChange={e =>
                handleUpdateState(prev => ({
                  ...prev,
                  expandImageOnClick: e.target.checked,
                }))
              }
            />
          </div>
        )}
      </div>
    );
  }

  return (
    <WebsiteBuilderPanelContent
      title={t("Slideshow settings")}
      closePanel={closeSettings}
      sections={[renderImagesSettings(), renderGeneralSettings(), renderMoreSettings()]}
    />
  );
}
